import React from "react"
import moment from "moment"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  BellAlertIcon,
  InboxIcon,
  HashtagIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline"
import useSpamStatistics from "./useSpamStatistics"
import TableKPICard from "aldoo-ra/Admin/CustomerSupport/TableKPICard"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"

// KPI Card Component with consistent styling
const KPICard = ({ title, value, icon, textColor, isHighlighted = false }) => (
  <div
    className={`bg-admin_paper rounded-xl p-6 shadow-sm hover:shadow-md ${
      isHighlighted ? "border border-admin_error" : ""
    }`}
  >
    <div>
      <Typography
        component="h2"
        className="h-[72px] text-admin_text text-[18px] xl:text-[20px] 2xl:text-[22px] font-normal leading-tight mb-4"
      >
        {title}
      </Typography>

      <div className="flex items-center justify-between mt-6">
        <Typography 
          component="output"
          variant="admin_h2" 
          className={`${textColor} mt-0`}
        >
          {value}
        </Typography>

        <div className="bg-admin_canvas rounded-lg p-3">{icon}</div>
      </div>
    </div>
  </div>
)

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-admin_paper shadow-md rounded border border-admin_bordered p-3">
        <Typography 
          variant="admin_label" 
          className="my-0"
        >
          {label}
        </Typography>

        {payload.map((entry, index) => (
          <Typography 
            key={index} 
            variant="admin_body1" 
            className={entry.color === '#EF4444' ? 'text-admin_error' : 'text-admin_success'}
          >
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </div>
    )
  }
  return null
}

export default function SpamOverview() {
  const { statistics, loading, error, lastUpdated, refresh } =
    useSpamStatistics()

  // Format daily stats for chart ensuring we have 7 days with most recent on right
  const formatDailyStats = () => {
    // Create an array of the last 7 days
    const last7Days = []
    const today = moment()

    // Create a map to quickly look up counts by date string
    const countsByDate = {}

    // If we have statistics, populate the map
    if (statistics?.dailyStats && statistics.dailyStats.length > 0) {
      statistics.dailyStats.forEach((item) => {
        countsByDate[item.date] = {
          blacklist: item.blacklist || 0,
          whitelist: item.whitelist || 0,
          total: item.total || 0
        }
      })
    }

    // Generate data for the last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = moment(today).subtract(i, "days")
      const dateStr = date.format("YYYY-MM-DD")
      const displayDate = date.format("MMM DD")
      const dailyData = countsByDate[dateStr] || { blacklist: 0, whitelist: 0, total: 0 }

      last7Days.push({
        date: displayDate,
        blocked: dailyData.blacklist,
        whitelisted: dailyData.whitelist,
        // Add a numeric value for proper sorting
        sortValue: 6 - i,
      })
    }

    // Sort by date, most recent last (will be on the right of the chart)
    return last7Days.sort((a, b) => a.sortValue - b.sortValue)
  }

  // Fallback when data is loading
  if (loading && !statistics) {
    return (
      <div className="animate-pulse p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="bg-admin_paper rounded-xl h-36 shadow-md"
            ></div>
          ))}
        </div>
        <div className="mt-8 bg-admin_paper rounded-xl h-72 shadow-md"></div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="bg-admin_paper rounded-xl h-80 shadow-md"
            ></div>
          ))}
        </div>
      </div>
    )
  }

  // Fallback when there's an error and no previous data
  if (error && !statistics) {
    return (
      <div className="p-8">
        <div className="bg-admin_error/30 rounded-lg p-4 mb-4">
          <Typography 
            variant="admin_txt1" 
            className="text-admin_error mb-2"
          >
            Error loading statistics: {error}
          </Typography>
        </div>

        <Button 
          onClick={refresh} 
          variant="admin_primary"
        >
          Retry
        </Button>
      </div>
    )
  }

  // Format the chart data
  const chartData = formatDailyStats()

  return (
    <div className="h-full bg-admin_canvas p-8">
      <div className="flex justify-between items-center mb-8">
        <Typography 
          component="h1" 
          variant="admin_h2"
        >
          Spam Protection Overview
        </Typography>

        <div className="flex items-center gap-4">
          {lastUpdated && (
            <div className="bg-admin_paper rounded-lg px-4 py-2 shadow-sm">
              <Typography variant="admin_txt1_dim">
                Last updated: {moment(lastUpdated).fromNow()}
              </Typography>
            </div>
          )}
          
          <Button
            onClick={refresh}
            variant="admin_secondary"
            disabled={loading}
            className="whitespace-nowrap"
          >
            {loading ? "Refreshing..." : "Refresh Data"}
          </Button>
        </div>
      </div>

      {/* Top Row - Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
        {/* Total Blocked Emails */}
        <KPICard
          title="Blocked Emails"
          value={statistics?.totalBlockedEmails || 0}
          textColor="text-admin_error"
          isHighlighted={true}
          icon={<ShieldExclamationIcon className="w-6 h-6 text-admin_error" />}
        />

        {/* Total Whitelisted Emails */}
        <KPICard
          title="Whitelisted Emails"
          value={statistics?.totalWhitelistedEmails || 0}
          textColor="text-admin_success"
          icon={<CheckCircleIcon className="w-6 h-6 text-admin_success" />}
        />

        {/* Blocked Today */}
        <KPICard
          title="Blocked Today"
          value={statistics?.blockedToday || 0}
          textColor="text-admin_warning"
          icon={<BellAlertIcon className="w-6 h-6 text-admin_warning" />}
        />

        {/* Whitelisted Today */}
        <KPICard
          title="Whitelisted Today"
          value={statistics?.whitelistedToday || 0}
          textColor="text-admin_success"
          icon={<CheckCircleIcon className="w-6 h-6 text-admin_success" />}
        />

        {/* Active Rules */}
        <KPICard
          title="Active Rules"
          value={statistics?.activeRules || 0}
          textColor="text-admin_primary"
          icon={<ShieldCheckIcon className="w-6 h-6 text-admin_primary" />}
        />

        {/* Total Activity */}
        <KPICard
          title="Total Activity"
          value={(statistics?.totalBlockedHits || 0) + (statistics?.totalWhitelistHits || 0)}
          textColor="text-admin_text"
          icon={<InboxIcon className="w-6 h-6 text-admin_text" />}
        />
      </div>

      {/* Daily Statistics Chart - Always show even if we don't have data yet */}
      <div className="mb-8 bg-admin_paper p-6 rounded-xl shadow-sm">
        <Typography 
          variant="admin_txt1_accent" 
          className="block text-admin_text mb-4"
        >
          Spam Protection Activity (Last 7 Days)
        </Typography>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fill: "#6B7280" }} />
              <YAxis
                tick={{ fill: "#6B7280" }}
                allowDecimals={false}
                // Start at 0 and have at least 5 ticks
                domain={[0, "auto"]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="blocked"
                name="Blocked Emails"
                fill="#EF4444"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="whitelisted"
                name="Whitelisted Emails"
                fill="#22C55E"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row - Table Cards for Domains and Rules */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Top Blocked Domains Table */}
        <TableKPICard
          title="Top Blocked Domains"
          data={statistics?.blockedDomains || []}
          keyField="domain"
          valueField="count"
          emptyMessage="No domains blocked yet"
          bgColor="bg-admin_paper"
          textColor="text-admin_text"
          icon={<InboxIcon className="w-6 h-6 text-admin_error" />}
          maxItems={5}
          loading={loading}
        />

        {/* Top Whitelisted Domains Table */}
        <TableKPICard
          title="Top Whitelisted Domains"
          data={statistics?.whitelistedDomains || []}
          keyField="domain"
          valueField="count"
          emptyMessage="No domains whitelisted yet"
          bgColor="bg-admin_paper"
          textColor="text-admin_text"
          icon={<CheckCircleIcon className="w-6 h-6 text-admin_success" />}
          maxItems={5}
          loading={loading}
        />

        {/* Most Triggered Rules Table */}
        <TableKPICard
          title="Most Triggered Rules"
          data={statistics?.topRules || []}
          keyField="description"
          valueField="hitCount"
          emptyMessage="No rules triggered yet"
          bgColor="bg-admin_paper"
          textColor="text-admin_text"
          icon={<HashtagIcon className="w-6 h-6 text-admin_primary" />}
          maxItems={5}
          loading={loading}
        />
      </div>

      {/* Show error as a banner if refreshing fails but we have old data */}
      {error && (
        <div className="mt-8 bg-admin_error/10 text-admin_error p-4 rounded-lg flex justify-between items-center shadow-md">
          <span>
            Error refreshing: {error}
          </span>

          <Button onClick={refresh} variant="admin_error">
            Retry
          </Button>
        </div>
      )}
    </div>
  )
}
